// AdvanceButton.tsx — FE_SPEC §8 · sticky advance bar for GatingMode=user_advance.
//
// Behavior contract (FE_SPEC §8 + P0-10 + P0-14):
//   - sticky bottom card with phase_summary preview + 30-min countdown.
//   - click → on_advance() (parent owns the POST /sessions/{id}/advance).
//   - in-flight ref guard: rapid double-click only fires once.
//   - on success the parent receives SSE phase_advanced and unmounts us via
//     `awaiting_advance=false` propagation; we render a spinner while in-flight.
//
// Note: this component does NOT call sessionAdvance directly — keeps the visual
// layer free of api/store coupling. FE-B wires the real POST + idempotency-key.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface AdvanceButtonProps {
  phase_name: string;
  phase_summary: string;
  on_advance: () => Promise<void> | void;
  disabled?: boolean;
  /** seconds until BE auto-fallthrough; default 1800 = 30 min. */
  countdown_seconds?: number;
}

const PREVIEW_LIMIT = 200;

function formatMmSs(total: number): string {
  const t = Math.max(0, Math.floor(total));
  const m = Math.floor(t / 60);
  const s = t % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

export default function AdvanceButton({
  phase_name,
  phase_summary,
  on_advance,
  disabled = false,
  countdown_seconds = 1800,
}: AdvanceButtonProps) {
  const { t } = useTranslation();
  const inflightRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(countdown_seconds);

  // Reset countdown if parent passes a new initial.
  useEffect(() => {
    setSecondsLeft(countdown_seconds);
  }, [countdown_seconds]);

  // Tick once per second; clamp at 0.
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = window.setInterval(() => {
      setSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [secondsLeft]);

  const onClick = useCallback(async () => {
    // ★ P0-14: in-flight guard · rapid double-click only sends one POST.
    if (inflightRef.current) return;
    if (disabled) return;
    inflightRef.current = true;
    setBusy(true);
    try {
      await on_advance();
    } finally {
      // Stay disabled in UI until parent unmounts us via awaiting_advance=false.
      // But release the ref so a re-mount can fire again.
      inflightRef.current = false;
    }
  }, [disabled, on_advance]);

  const truncated =
    phase_summary.length > PREVIEW_LIMIT && !expanded
      ? `${phase_summary.slice(0, PREVIEW_LIMIT)}…`
      : phase_summary;

  const isDisabled = disabled || busy;

  return (
    <div
      data-testid="advance-button"
      data-phase={phase_name}
      data-busy={busy ? 'true' : 'false'}
      className="sticky bottom-0 left-0 right-0 mt-6 border-t border-hairline bg-paper px-4 py-3 shadow-md"
    >
      {phase_summary && (
        <div className="mb-2">
          <p className="text-xs font-mono uppercase tracking-meta text-ink-3 mb-1">
            {t('sessionPaused.title', { defaultValue: 'Phase summary' })}
          </p>
          <p className="text-sm text-ink leading-relaxed">{truncated}</p>
          {phase_summary.length > PREVIEW_LIMIT && (
            <button
              type="button"
              className="mt-1 text-xs underline text-azure-700"
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded
                ? t('button.collapse', { defaultValue: 'Collapse' })
                : t('button.expand', { defaultValue: 'Expand' })}
            </button>
          )}
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        <span
          className="text-xs font-mono text-ink-3"
          data-testid="advance-button-countdown"
        >
          {t('button.advance_countdown', {
            defaultValue: 'Auto-continue in {{time}}',
            time: formatMmSs(secondsLeft),
          })}
        </span>
        <button
          type="button"
          className="px-4 py-2 rounded-md bg-jade text-ink-inv font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => {
            void onClick();
          }}
          disabled={isDisabled}
          aria-busy={busy}
          data-testid="advance-button-cta"
        >
          {busy
            ? t('button.advance_busy', { defaultValue: 'Advancing…' })
            : t('button.advance', { defaultValue: 'Continue to next phase' })}
        </button>
      </div>
    </div>
  );
}
